import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tag, TrendingUp, Target, Users, MousePointerClick } from "lucide-react";

interface TagStats {
  campaign_tag: string;
  impressions: number;
  conversions: number;
  conversion_rate: number;
  quiz_only_conversions: number;
  quiz_only_conversion_rate: number;
  lead_conversions: number;
  lead_conversion_rate: number;
  b2c_exits: number;
  b2c_exit_rate: number;
  cta_clicks: number;
  cta_click_rate: number;
}

interface HeadlineTagStatsProps {
  tagStats: TagStats[];
}

export const HeadlineTagStats = ({ tagStats }: HeadlineTagStatsProps) => {
  if (!tagStats || tagStats.length === 0) {
    return null;
  }

  // Sort by conversion rate descending
  const sortedStats = [...tagStats].sort((a, b) => b.conversion_rate - a.conversion_rate);
  const bestTag = sortedStats[0];

  return (
    <Card className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Statystyki kampanii (według tagów)
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Analiza wydajności kampanii według przypisanych tagów
          </p>
        </div>
        {bestTag && (
          <div className="text-right">
            <div className="text-sm text-muted-foreground">Najlepszy tag</div>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="default" className="text-sm">
                {bestTag.campaign_tag}
              </Badge>
              <span className="text-lg font-semibold text-primary">
                {bestTag.conversion_rate}%
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tag kampanii</TableHead>
              <TableHead className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <Users className="h-4 w-4" />
                  Wyświetlenia
                </div>
              </TableHead>
              <TableHead className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <Target className="h-4 w-4" />
                  Konwersje
                </div>
              </TableHead>
              <TableHead className="text-right">Quiz</TableHead>
              <TableHead className="text-right">Lead</TableHead>
              <TableHead className="text-right text-orange-600 dark:text-orange-400">
                <div className="flex items-center justify-end gap-1">
                  🚪 B2C Exit
                </div>
              </TableHead>
              <TableHead className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <MousePointerClick className="h-4 w-4" />
                  CTA
                </div>
              </TableHead>
              <TableHead className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <TrendingUp className="h-4 w-4" />
                  CR %
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedStats.map((stat) => (
              <TableRow key={stat.campaign_tag}>
                <TableCell>
                  <Badge variant="outline">{stat.campaign_tag}</Badge>
                </TableCell>
                <TableCell className="text-right font-medium">
                  {stat.impressions}
                </TableCell>
                <TableCell className="text-right font-medium">
                  {stat.conversions}
                  <span className="text-xs text-muted-foreground ml-1">
                    ({stat.conversion_rate}%)
                  </span>
                </TableCell>
                <TableCell className="text-right text-sm">
                  {stat.quiz_only_conversions}
                  <span className="text-xs text-muted-foreground ml-1">
                    ({stat.quiz_only_conversion_rate}%)
                  </span>
                </TableCell>
                <TableCell className="text-right text-sm">
                  {stat.lead_conversions}
                  <span className="text-xs text-muted-foreground ml-1">
                    ({stat.lead_conversion_rate}%)
                  </span>
                </TableCell>
                <TableCell className="text-right text-sm">
                  <span className="text-orange-600 dark:text-orange-400 font-medium">
                    {stat.b2c_exits}
                  </span>
                  <span className="text-xs text-orange-600/70 dark:text-orange-400/70 ml-1">
                    ({stat.b2c_exit_rate}%)
                  </span>
                </TableCell>
                <TableCell className="text-right text-sm">
                  <span className="text-purple-600 font-medium">
                    {stat.cta_clicks}
                  </span>
                  <span className="text-xs text-muted-foreground ml-1">
                    ({stat.cta_click_rate}%)
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <span className={`font-semibold ${
                    stat.conversion_rate >= bestTag.conversion_rate 
                      ? 'text-primary' 
                      : ''
                  }`}>
                    {stat.conversion_rate}%
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};
